<div class="group relative w-full shrink-0 overflow-hidden rounded-lg" [class.video-landscape]="isHorizontal()"
    [class.video-portrait]="!isHorizontal()">
    @if (!playbackActive()) {
    <div class="relative h-full min-h-0 w-full">
        @if (showCoverSkeleton()) {
        <div class="video-preview-cover-skeleton absolute inset-0 z-5 rounded-lg" role="status" aria-live="polite"
            aria-busy="true">
            <span class="sr-only">Loading thumbnail</span>
            <div class="video-preview-skeleton-base absolute inset-0 rounded-lg"></div>
            <div class="video-preview-skeleton-shimmer rounded-lg"></div>
        </div>
        }
        @if (thumbnailUrl(); as coverSrc) {
        <img [src]="coverSrc" alt="" loading="eager" decoding="async"
            class="pointer-events-none absolute left-0 top-0 h-px w-px opacity-0" (load)="onCoverPreloadDone()"
            (error)="onCoverPreloadDone()" />
        }
        <atom-video-thumbnail [videoUrl]="playbackUrl()" [videoFormat]="videoFormatForAtom()"
            [thumbnailURL]="thumbnailUrl()" [cdnKey]="thumbnailCdnKey()"></atom-video-thumbnail>
        <div
            class="pointer-events-none absolute inset-0 z-15 flex items-center justify-center bg-black/0 transition-opacity duration-200 group-hover:bg-black/25 play-overlay-transition group-focus-within:bg-black/25">
            <button type="button"
                class="pointer-events-auto flex size-14 items-center justify-center rounded-full bg-white/90 text-accent shadow-md opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary play-overlay-transition"
                (click)="onPlayClick()" [attr.aria-label]="'Play video: ' + video().title">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
                    class="size-7 translate-x-0.5" aria-hidden="true">
                    <path d="M8 5.14v14l11-7-11-7z" />
                </svg>
            </button>
        </div>
    </div>
    } @else {
    <div
        class="relative video-inner w-full"
        [class.video-inner-portrait]="!isHorizontal()"
        [class.video-inner-landscape]="isHorizontal()">
        <video #videoPlayer playsinline class="absolute inset-0 z-0 h-full w-full object-cover rounded-lg bg-black"
            (play)="onVideoPlay()" (pause)="onVideoPause()" (timeupdate)="onTimeUpdate()"
            (loadedmetadata)="onLoadedMetadata()" (volumechange)="onVolumeChange()">
        </video>
        <div
            class="w-full inline-video-controls absolute inset-x-0 bottom-0 z-20 flex flex-row items-center gap-2 bg-linear-to-t from-black/80 to-transparent px-2 pb-2 pt-6 text-white">
            <button type="button"
                class="inline-flex size-11 shrink-0 items-center justify-center rounded-md hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                (click)="onControlBarPlayPause($event)" [attr.aria-label]="isPlaying() ? 'Pause' : 'Play'">
                @if (isPlaying()) {
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-6"
                    aria-hidden="true">
                    <path
                        d="M8 5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H8Zm7 0a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-1Z" />
                </svg>
                } @else {
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
                    class="size-6 translate-x-0.5" aria-hidden="true">
                    <path d="M8 5.14v14l11-7-11-7z" />
                </svg>
                }
            </button>
            <div class="flex-1 flex flex-col gap-y-0.5 items-start justify-center">
                <span class="shrink-0 tabular-nums text-xs font-medium text-white/90" aria-live="polite">
                    {{ formatTime(currentTimeSec()) }}
                </span>
                <div class="min-w-0 flex-1 flex flex-col justify-center py-1 w-full" role="group" aria-label="Seek">
                    <div tabindex="0" role="slider" [attr.aria-valuenow]="ariaTimeNow()" [attr.aria-valuemin]="0"
                        [attr.aria-valuemax]="ariaDurationMax()" aria-label="Video progress"
                        class="progress-hit cursor-pointer rounded-md px-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                        (mousedown)="onProgressPointerDown($event)" (touchstart)="onProgressPointerDown($event)"
                        (keydown)="onProgressKeyDown($event)">
                        <div class="h-1.5 w-full rounded-full bg-white/30">
                            <div class="h-full rounded-full bg-primary/65" [style.width.%]="progressPercent()"></div>
                        </div>
                    </div>
                </div>
            </div>

            <button type="button"
                class="inline-flex size-11 shrink-0 items-center justify-center rounded-md hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                (click)="onToggleMute($event)" [attr.aria-label]="isMuted() ? 'Unmute' : 'Mute'">
                @if (isMuted()) {
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-5" aria-hidden="true">
                    <path d="M11 5 6 9H2v6h4l5 4V5Z" />
                    <line x1="23" x2="17" y1="9" y2="15" />
                    <line x1="17" x2="23" y1="9" y2="15" />
                </svg>
                } @else {
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-5" aria-hidden="true">
                    <path d="M11 5 6 9H2v6h4l5 4V5Z" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                </svg>
                }
            </button>

        </div>
    </div>
    }
</div>
